import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { requestProviderById } from '~/store/provider';
import {
  useNavigation,
  useProvider,
  useLocalStorage,
  useHasAccess,
  AccessList,
  useUser,
} from '~/Hooks';
import { Route } from '~/Constants/Routes.constants';
import {
  IFitoList,
  IFitoListSettings,
} from '~/Components/FitoList/FitoListUncontrolled/interfaces';
import { IStudio } from '~/Models/DTO/Studio.model';
import { ProviderAccessType } from '~/Models/DTO/Provider.model';
import { USCCircle } from '~/Components/Icons/3rdParty/USCCircle';
import { FitogramCircle } from '~/Components/Icons/brand/FitogramCircle';
import { makeStyles } from '~/UIKit/Style';
import { CellTypes } from '~/Components/DataTable/DataTable.types';
import { GRAY_USC } from '~/Constants/Colors.constants';
import { FONT_SIZE_MEDIUM_INBETWEEN, FONT_SIZE_SMALL } from '~/Constants/UI.constants';
import { useRequestProvidersSearch } from '~/requests';
import { UserType } from '~/Models/DTO/User.model';

export interface IProviderFilter {
  name?: string;
  organisationVenueId?: string;
  page?: number;
  pageSize?: number;
}
export function useProviderSelection({ searchText }: { searchText: string }) {
  // hooks
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const { hasAccess } = useHasAccess();
  const { navigate } = useNavigation();
  const { isCurrentProviderLoading, currentProvider } = useProvider();
  const { redirectUrl, setProviderUUIdAfterARedirectIsCleaned } = useLocalStorage();
  const { DANGEROUSLY_isSuperAdmin: isSuperAdmin, currentUser } = useUser();

  // state
  const [isProviderSelected, setProviderSelected] = useState(false);

  // actions
  const getProviderById = bindActionCreators(requestProviderById, dispatch);

  const useGetProviders = ({ pageSize, ...query }: { pageSize: number; query: any }) =>
    useRequestProvidersSearch({
      ...{ ...query, pageSize: searchText?.length ? 50 : pageSize },
      accessType: [UserType.PARTNERGRAM].includes(currentUser?.utype || UserType.PROVIDER)
        ? ProviderAccessType.PARTNER_TOOL
        : false,
      IncludeAvailableProvidersOnly:
        ![UserType.SUPER_ADMIN, UserType.PARTNERGRAM].includes(
          currentUser?.utype || UserType.PROVIDER,
        ) || !searchText?.length,
    });
  useEffect(() => {
    //if user select a provider, navigate to dashboard
    if (isProviderSelected && currentProvider && !isCurrentProviderLoading) {
      if (redirectUrl) {
        setProviderUUIdAfterARedirectIsCleaned();
      }
      navigate(Route.PROVIDER_DETAIL, { domain: currentProvider.domain });
    }
  }, [currentProvider, isProviderSelected, isCurrentProviderLoading]);

  const handleSelect = (uuid: string) => () => {
    getProviderById(uuid);
    setProviderSelected(true);
  };

  const handleAddStudio = useCallback(() => navigate(Route.ONBOARDING_POSTLOGIN), [
    currentProvider,
  ]);

  const settings: IFitoListSettings = {
    search: false,
    rearrange: true,
    pagination: true,
  };

  const getPicture = (item: IStudio) => {
    if (!hasAccess(AccessList.SEE_STUDIO_TYPE_ICON_ON_PROVIDER_SELECT)) {
      return undefined;
    }
    if (item.accessType === ProviderAccessType.FULL) {
      return (
        <div className={classes.icons}>
          <FitogramCircle />
        </div>
      );
    }
    if (item.accessType === ProviderAccessType.PARTNER_TOOL) {
      return (
        <div className={classes.icons}>
          <USCCircle />
        </div>
      );
    }
  };
  const tableDefinition = {
    config: { useSelectableRows: true },
    columns: [
      {
        label: ' ',
        propertyIds: ['name', 'organisationVenueId', 'accessType', 'uuid'],
        cellType: CellTypes.String,
        formatter: ({ name, organisationVenueId, accessType, uuid }: IStudio) => (
          <div className={classes.titleContainer} onClick={() => handleSelect(uuid)()}>
            {getPicture({ accessType } as any)}
            <div className={classes.name}>{name}</div>
            {!!organisationVenueId && (
              <div className={classes.venueId}>{` | ID:${organisationVenueId}`}</div>
            )}
          </div>
        ),
      },
    ],
  };

  const setItemValues: (item: IStudio) => IFitoList = (item: IStudio) => ({
    id: item.uuid,
    header: `${item.name}${item.organisationVenueId ? ` | #${item.organisationVenueId}` : ''}`,
    subheader: !!item?.organisationVenueId ? `ID: ${item.organisationVenueId}` : '',
    picture: getPicture(item),
    onClick: () => handleSelect(item.uuid)(),
  });

  const searchTypes = [
    {
      label: 'form.field.name',
      value: 'name',
    },
    {
      label: 'app.venueid',
      value: 'organisationVenueId',
    },
  ];

  return {
    settings,
    setItemValues,
    searchTypes,
    handleAddStudio,
    isSuperAdmin,
    tableDefinition,
    useRequestProvidersSearch: useGetProviders,
    restProps: {
      settings: {
        ...settings,
        pagination: !searchText?.length,
        paginationSettings: { startAt: 0, pageField: 'page', sizeField: 'pageSize' },
      },
      pageSize: !searchText?.length ? 20 : 50,
      infiniteScroll: !searchText?.length ? true : undefined,
    } as any,
  };
}

const useStyles = makeStyles(
  (theme) => ({
    icons: {
      height: 32,
      width: 32,
      '& > svg': {
        fontSize: '32px !important',
        height: 'inherit',
        width: 'inherit',
      },
    },
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      flexDirection: 'column',
      height: '100%',
      width: '100%',
      margin: 0,
      [theme.breakpoints.up('md')]: {
        flexDirection: 'row',
      },
    },
    name: {
      marginLeft: '10px',
      fontSize: FONT_SIZE_MEDIUM_INBETWEEN,
    },
    venueId: {
      marginLeft: '10px',
      color: GRAY_USC,
      fontSize: FONT_SIZE_SMALL,
      [theme.breakpoints.up('md')]: {
        fontSize: FONT_SIZE_MEDIUM_INBETWEEN,
      },
    },
  }),
  { name: 'ProviderSelection' },
);
